import { useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../store/auth';
import { record } from '../observability';
import { onResume, signalResume } from '../lib/resume';
import { signOutSince, signOutStamp } from '../lib/signOuts';
import { API_BASE } from '../utils/appEnv';
import type { ServerToClientEvents, ClientToServerEvents } from '../types';

/** The socket with the two event maps of `types/index.ts` (#664, F23): `on`
 *  and `off` check the event name and the payload, and `emit` checks the join. */
type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/** The time from a reconnect to the reload of the live views (#499): the
 *  room joins of `replayRooms` reach the server first. */
const JOIN_SETTLE_MS = 500;
/** A backend restart reconnects every open tab at once. A random wait up to
 *  this spreads their reloads over the first seconds of the new backend and
 *  its database pool. */
const RELOAD_JITTER_MS = 1_500;
/** #501 — the least time between two token refreshes for an anonymous socket. */
const AUTH_REFRESH_INTERVAL_MS = 60_000;

// Module-level singleton socket — shared across all components. Listeners
// that should live for the lifetime of the socket are attached inside the
// creation block below so they're registered exactly once (no flag-based
// bookkeeping, no per-render rebinds).
let globalSocket: AppSocket | null = null;

// The identity the live socket was opened for: a user id, or null for an
// anonymous viewer. `undefined` means no socket has been opened yet.
//
// A bare `user === null` cannot tell "signed out just now" from "never signed
// in", and the old code treated both as logout — so a GUEST never got a socket
// at all. Public scoreboards, live round totals, the verdict and the
// auto-advance to the next bout were all dead for signed-out viewers, with no
// error anywhere. The backend always accepted anonymous sockets
// (sockets/handlers.ts io.use calls next() with no token); only the client
// refused to open one.
let socketIdentity: string | null | undefined = undefined;

/** A room of the server (#664, F49). The key is the name that `decodeKey()` reads. */
export type RoomKey = `tournament:${string}` | `bout:${string}` | `bout:${string}:officials`;

// Ref-counted set of rooms this client wants to be in. Key serializations:
//   bout:<id>            → emit('join:bout', id) / emit('leave:bout', id)
//   bout:<id>:officials  → emit('join:bout:officials', id) / emit('leave:bout:officials', id)
//   tournament:<id>      → emit('join:tournament', id) / emit('leave:tournament', id)
// On every `connect` event we replay every entry with count > 0, because
// socket.io tears down server-side room membership on disconnect — without
// the replay a network blip / backend rolling restart silently strands the
// client outside its rooms with no recovery short of a page reload.
const desiredRooms = new Map<string, number>();

function decodeKey(key: string): { event: 'bout' | 'bout:officials' | 'tournament'; id: string } | null {
  if (key.startsWith('bout:') && key.endsWith(':officials')) {
    return { event: 'bout:officials', id: key.slice('bout:'.length, -':officials'.length) };
  }
  if (key.startsWith('bout:')) return { event: 'bout', id: key.slice('bout:'.length) };
  if (key.startsWith('tournament:')) return { event: 'tournament', id: key.slice('tournament:'.length) };
  return null;
}

function emitJoin(key: string) {
  const d = decodeKey(key);
  if (!d || !globalSocket) return;
  // socket.io buffers outgoing emits while disconnected and flushes on next
  // connect, but we ALSO replay on `connect` for correctness across full
  // disconnect/reconnect cycles where the buffered emit would target the
  // dead session. Belt-and-braces.
  globalSocket.emit(`join:${d.event}`, d.id);
}

function emitLeave(key: string) {
  const d = decodeKey(key);
  if (!d || !globalSocket) return;
  globalSocket.emit(`leave:${d.event}`, d.id);
}

function replayRooms() {
  desiredRooms.forEach((count, key) => {
    if (count > 0) emitJoin(key);
  });
}

// #499 — after a phone lock the heartbeat timer of engine.io can fire late,
// and only an emit checks the ping deadline on the wall clock. A resume sends
// the joins again: a live socket ignores them (a room is a Set), and a socket
// that died in the lock closes at once and reconnects, so its events come
// back sooner than the ping timeout.
onResume(() => { if (globalSocket?.connected) replayRooms(); });

// Always emit on join — even when count > 1. A second owner of the same room
// (BoutDetail and BoutCard both own `bout:<id>:officials`) sends the join again,
// and the server runs its DB-gated check again. Server-side `socket.join` is
// idempotent (Set semantics), so a duplicate emit only costs that one query.
// A page never calls joinRoom itself: `useRooms()` joins and leaves in pairs.
function joinRoom(key: RoomKey) {
  desiredRooms.set(key, (desiredRooms.get(key) ?? 0) + 1);
  emitJoin(key);
}

function leaveRoom(key: RoomKey) {
  const cur = desiredRooms.get(key) ?? 0;
  if (cur === 0) return;
  if (cur === 1) {
    desiredRooms.delete(key);
    emitLeave(key);
  } else {
    desiredRooms.set(key, cur - 1);
  }
}

export function useSocket(): AppSocket | null {
  // Phase 2: socket auth rides on the lmf_access cookie via withCredentials.
  // The Zustand `user` blob is the FE's signal for "logged in"; null = logout.
  const user = useAuthStore(s => s.user);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    // Identity change tears the socket down and forgets every desired room.
    // That covers logout (id -> null) AND a different user signing in on the
    // same tab. Without it the previous identity's room intents replay through
    // replayRooms — the backend re-checks DB-gated `:officials` rooms (so no
    // data leak), but public `bout:` / `tournament:` rooms have no per-event
    // auth and would keep feeding events under the new identity.
    //
    // A guest is NOT a teardown case. `uid === null` with no live socket means
    // "open an anonymous one", which is what a public scoreboard needs.
    const uid = user?.id ?? null;
    if (globalSocket && socketIdentity !== uid) {
      globalSocket.disconnect();
      globalSocket = null;
      desiredRooms.clear();
    }
    if (!globalSocket) {
      globalSocket = io(API_BASE, {
        // Cookie auth — handshake carries lmf_access; backend's io.use()
        // middleware reads it instead of the legacy handshake.auth.token.
        withCredentials: true,
        // #459 (operator decision, 2026-09-24): WebSocket only. The polling
        // "fallback" could not work: engine.io-client 6 moves to the next
        // transport only with `tryAllTransports`, the Socket.io CORS sent no
        // credentials, and polling needs session affinity across replicas.
        // On a network that blocks WebSocket, live updates stop; a reload
        // shows the current state.
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
      });
      socketIdentity = uid;
      // Force re-render once socket is ready so consumers get a non-null reference
      globalSocket.on('connect', () => forceUpdate(n => n + 1));
      // Re-emit every desired-room join on every (re)connect. socket.io tears
      // down server-side room membership on disconnect, so a network blip /
      // backend rolling restart leaves the client connected but in zero rooms
      // without this replay.
      globalSocket.on('connect', replayRooms);
      // #499 — a connect after a drop or after a failed attempt follows a gap:
      // the page loaded over HTTP while this socket was not in its rooms, and
      // nothing sends the events of the gap again. The live views reload
      // after the joins above reach the server (JOIN_SETTLE_MS).
      let gap = false;
      globalSocket.on('connect_error', () => { gap = true; });
      globalSocket.on('disconnect', () => { gap = true; });
      globalSocket.on('connect', () => {
        if (gap) {
          setTimeout(() => signalResume('socket'), JOIN_SETTLE_MS + Math.random() * RELOAD_JITTER_MS);
        }
        gap = false;
      });
      // Telemetry: surface disconnect cause + reconnect lag so we can see how
      // often network blips strand clients in real-world tournament use, and
      // whether the ref-counted desiredRooms replay is actually firing.
      globalSocket.on('disconnect', (reason: string) => {
        record({ event: 'socket_disconnect', props: { reason } });
      });
      globalSocket.io.on('reconnect', (attempt: number) => {
        record({ event: 'socket_reconnect', props: { attempt: String(attempt) } });
      });
      // #501 — the browser deletes the 1 h `lmf_access` cookie at its expiry,
      // so a socket that connects after that carries no token and joins as a
      // guest: no user room, and the officials room refuses it (27 times on
      // the tournament day of 2026-09-26). The server sends `auth:anonymous`
      // to every guest socket; a signed-in tab refreshes the token and
      // connects again, so the new handshake carries the new cookie. At most
      // once a minute, so a cookie that the socket does not get cannot loop.
      let lastAuthRefresh = -Infinity;
      globalSocket.on('auth:anonymous', async () => {
        if (!useAuthStore.getState().user) return;
        if (performance.now() - lastAuthRefresh < AUTH_REFRESH_INTERVAL_MS) return;
        lastAuthRefresh = performance.now();
        // #666 — The stamp before the refresh.
        const stamp = signOutStamp();
        try {
          const { tryRefreshToken } = await import('../api/client');
          const ok = await tryRefreshToken();
          // #666 — A sign-out runs now, or one began or ended during the
          // refresh. While a sign-out runs, `tryRefreshToken()` answers false
          // and sends no request: that answer is no failure of a refresh. No
          // event, and the socket does not connect again.
          if (signOutSince(stamp)) return;
          record({ event: 'socket_auth_refreshed', props: { ok: String(ok) } });
          // A failed refresh does nothing more here: the next authenticated
          // call of the tab runs the expiry path of the interceptor. A public
          // page (the ring TV) makes none, so it is never sent to /login.
          if (ok) globalSocket?.disconnect().connect();
        } catch { /* the next connect tries again */ }
      });
      // Refresh JWT when admin approves a role. Registered exactly once at
      // socket creation — socket.io dedupes across reconnects automatically.
      // Phase 2: refresh sets new cookies as a side-effect; we re-fetch /me
      // to pick up the bumped roles, then bounce the socket so its handshake
      // carries the fresh lmf_access cookie.
      globalSocket.on('auth:roles_changed', async () => {
        // #666 — A tab with no signed-in user starts no refresh. While a
        // sign-out runs, tryRefreshToken() answers false and starts none.
        if (!useAuthStore.getState().user) return;
        // #666 — The stamp before the first request: a sign-out after it
        // makes the answer of getMe() old.
        const stamp = signOutStamp();
        try {
          // The single-flight refresh (#455): a self-granted role sends this
          // event to the tab that asked, where a 401 can start a refresh at
          // the same moment. Two refreshes with one cookie can end the session.
          const { tryRefreshToken, getMe } = await import('../api/client');
          if (!(await tryRefreshToken())) {
            // #459 — the session can be over: a password reset or an erasure
            // sends this event too. An authenticated read then runs the
            // interceptor's expiry path, which signs this tab out. A network
            // failure leaves the tab as it is.
            const me = await getMe().catch(() => null);
            // The interceptor refreshed on its own: apply the new roles. The
            // socket connects again only when the store accepts the answer.
            if (me && useAuthStore.getState().refreshAuth(me.data, stamp) && globalSocket) {
              globalSocket.disconnect().connect();
            }
            return;
          }
          const meRes = await getMe();
          if (useAuthStore.getState().refreshAuth(meRes.data, stamp) && globalSocket) {
            globalSocket.disconnect().connect();
          }
        } catch { /* ignore — user will get fresh cookies on next login */ }
      });
    }
    // Reconnect cycle on login/logout — the handshake on .connect() picks up
    // the current cookie jar automatically (withCredentials).
  }, [user]);

  return globalSocket;
}

/** The listeners that one effect puts on the socket (#664, F83): an event name
 *  of `ServerToClientEvents` and its listener. `tsc` checks each key and the
 *  payload of its listener. */
export type SocketHandlers = { [E in keyof ServerToClientEvents]?: ServerToClientEvents[E] };

/** #664 (F83) — put the listeners of `handlers` on the socket, and return the
 *  function that removes them. The function calls `off` with the SAME
 *  reference that `on` received. An `off` with no listener removes every
 *  listener of that event on the shared socket, including those of other
 *  components (the `bout:public_scores_updated` leak of 2026-04). A page never
 *  calls `on` or `off` itself (`socket-glue-ratchet.spec.ts`).
 *
 *  Use it inside the effect that owns the handlers. Call the result in the
 *  cleanup. The effect keeps its dependencies and its closures, and each
 *  handler keeps its own scope guard. A `null` socket (the first render) binds
 *  nothing. One call holds one listener for each event: call it twice for two
 *  listeners of one event. */
export function bindSocket(socket: AppSocket | null, handlers: SocketHandlers): () => void {
  if (!socket) return () => {};
  // Read the map once: the cleanup must remove what this call added.
  const bound = (Object.entries(handlers) as Array<[keyof ServerToClientEvents, ServerToClientEvents[keyof ServerToClientEvents] | undefined]>)
    .flatMap(([event, listener]) => (listener ? [{ event, listener }] : []));
  for (const { event, listener } of bound) socket.on(event, listener);
  return () => { for (const { event, listener } of bound) socket.off(event, listener); };
}

/** #664 (F49) — the rooms that a component wants while it is mounted. One
 *  effect joins each key and leaves each key in its cleanup, so a join cannot
 *  run without its leave. `useRooms()` leaves out a key that is `false`, `null`
 *  or `undefined` (a gate: `canSee && room`). A key that is added or removed
 *  joins or leaves with the others. The effect runs again for a new socket: a
 *  change of the identity clears `desiredRooms`, so only a new run joins again.
 *  The list is compared as one string (JSON, so that no id can split it). */
export function useRooms(keys: ReadonlyArray<RoomKey | false | null | undefined>): void {
  const socket = useSocket();
  const joined = JSON.stringify(keys.filter((k): k is RoomKey => !!k));
  useEffect(() => {
    if (!socket) return;
    const rooms = JSON.parse(joined) as RoomKey[];
    rooms.forEach(joinRoom);
    return () => rooms.forEach(leaveRoom);
  }, [socket, joined]);
}

/** #664 (F49) — send the join of the officials room again. The rejoin adds
 *  NO reference.
 *
 *  The server runs its DB-gated check on each join. The claim of a seat (the
 *  `bout_jury` row or the `bout_referees` row did not exist when the page
 *  joined) changes the answer of that check. A roster event
 *  (`bout:referee_joined`, `bout:officials_changed`) changes it too.
 *
 *  The page owns the room through `useRooms()` and leaves it once. A rejoin
 *  that adds a reference has no leave, so the page would never leave the room.
 *  When no page owns the room, the rejoin does nothing. */
export function rejoinBoutOfficialsRoom(boutId: string) {
  const key: RoomKey = `bout:${boutId}:officials`;
  if ((desiredRooms.get(key) ?? 0) > 0) emitJoin(key);
}

/** The live bout of a ring display changes while the page stays open. Its id
 *  is stored in a ref of `useRingFeed.ts`, which joins and leaves it with
 *  direct calls. Any other room is a key of `useRooms()`. */
export function joinBoutRoom(boutId: string) {
  joinRoom(`bout:${boutId}`);
}

export function leaveBoutRoom(boutId: string) {
  leaveRoom(`bout:${boutId}`);
}
